import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {catchError} from 'rxjs/operators';
import {JsonApiResponse} from '../../shared/models/JsonApiResponse';
import {environment} from '../../../environments/environment';
import {HttpErrorHandler} from '../../http.error.handler';
import {
  BaseObjectClass,
  CartegraphConfiguration,
  CartegraphFeature,
  CartegraphFeatureFlag,
  CartegraphFiltersSettings, CartegraphHourSettings, CartegraphImportSettings,
  CartegraphObservationSettings,
  CartegraphProcessingNotification,
  CartegraphSyncTask, CartegraphTaskActivitySettings,
  CartegraphTaskDefaults, CartegraphTaskIdGenerationSettings,
  CgDatasetClassField,
  CgDatasetClassFieldRequest, CgDatasetEntry,
  CgDatasetList,
  CgDatasetListRequest,
  CgImportTask,
  EquipmentClass,
  ImportResultRow,
  LaborClass, Material, MaterialLocation, VehicleEquipment
} from '../../shared/models/cartegraph.model';
import {Page} from '../../shared/models/Page';
import {EMPTY, firstValueFrom, Observable, of} from 'rxjs';

const MOCK_CARTEGRAPH_CONFIGURATION = false;
const MOCK_MANUAL_MATERIAL_LOG = true;

@Injectable({
  providedIn: 'root'
})
export class CartegraphManagementService {

  constructor(private http: HttpClient) {
  }

  getConfiguration() {
    if (MOCK_CARTEGRAPH_CONFIGURATION) {
      return Promise.resolve(
        {
          data: {
            settings: {
              features: [
                {feature: CartegraphFeature.MANUAL_MATERIAL_LOG, enabled: MOCK_MANUAL_MATERIAL_LOG},
                {feature: CartegraphFeature.SEND_MATERIAL_LOG, enabled: MOCK_MANUAL_MATERIAL_LOG},
              ]
            }
          } as CartegraphConfiguration
        } as JsonApiResponse<CartegraphConfiguration>
      );
    }
    return firstValueFrom(this.http.get<JsonApiResponse<CartegraphConfiguration>>(`${environment.services.cartegraph}v1/config`)
      .pipe(
        catchError((err: any) => {
          const status = err?.status ?? err?.statusCode ?? err?.error?.status;
          if (status == 404 || Number(status) === 404 || err?.statusText === 'Not Found') {
            return of({ data: null } as JsonApiResponse<CartegraphConfiguration>);
          }
          return HttpErrorHandler.handleError(err);
        })
      ));
  }

  updateConfiguration(cartegraphUpdate: {}): Promise<JsonApiResponse<CartegraphConfiguration>> {
    return firstValueFrom(this.http.put<JsonApiResponse<CartegraphConfiguration>>(`${environment.services.cartegraph}v1/config`,
      cartegraphUpdate
    ).pipe(
      catchError(HttpErrorHandler.handleError) // then handle the error
    ));
  }

  updateNotifications(notifications: CartegraphProcessingNotification): Promise<JsonApiResponse<CartegraphConfiguration>> {
    return firstValueFrom(
      this.http.put<JsonApiResponse<CartegraphConfiguration>>(
        `${environment.services.cartegraph}v1/config/notifications`,
        notifications
      ).pipe(
        catchError(HttpErrorHandler.handleError) // then handle the error
      ));
  }

  updateFeatures(features: CartegraphFeatureFlag[]): Promise<JsonApiResponse<CartegraphConfiguration>> {
    return firstValueFrom(
      this.http.put<JsonApiResponse<CartegraphConfiguration>>(
        `${environment.services.cartegraph}v1/config/features`,
        features
      ).pipe(
        catchError(HttpErrorHandler.handleError) // then handle the error
      ));
  }

  updateDefaults(settings: CartegraphTaskDefaults): Promise<JsonApiResponse<CartegraphConfiguration>> {
    return firstValueFrom(
      this.http.put<JsonApiResponse<CartegraphConfiguration>>(
        `${environment.services.cartegraph}v1/config/settings/defaults`,
        settings
      ).pipe(
        catchError(HttpErrorHandler.handleError) // then handle the error
      ));
  }

  updateFilters(filters: CartegraphFiltersSettings) {
    return firstValueFrom(
      this.http.put<JsonApiResponse<CartegraphConfiguration>>(
        `${environment.services.cartegraph}v1/config/settings/filters`,
        filters
      ).pipe(
        catchError(HttpErrorHandler.handleError) // then handle the error
      ));
  }

  updateImport(filters: CartegraphImportSettings) {
    return firstValueFrom(
      this.http.put<JsonApiResponse<CartegraphConfiguration>>(
        `${environment.services.cartegraph}v1/config/settings/import`,
        filters
      ).pipe(
        catchError(HttpErrorHandler.handleError) // then handle the error
      ));
  }

  updateObservations(observations: CartegraphObservationSettings): Promise<JsonApiResponse<CartegraphConfiguration>> {
    return firstValueFrom(
      this.http.put<JsonApiResponse<CartegraphConfiguration>>(
        `${environment.services.cartegraph}v1/config/settings/observations`,
        observations
      ).pipe(
        catchError(HttpErrorHandler.handleError) // then handle the error
      ));
  }

  updateTaskActivities(taskActivitySettings: CartegraphTaskActivitySettings): Promise<JsonApiResponse<CartegraphConfiguration>> {
    return firstValueFrom(
      this.http.put<JsonApiResponse<CartegraphConfiguration>>(
        `${environment.services.cartegraph}v1/config/settings/task-activities`,
        taskActivitySettings).pipe(
        catchError(HttpErrorHandler.handleError) // then handle the error
      ));
  }

  updateTaskIdGenerationConfig(taskIdSettings: CartegraphTaskIdGenerationSettings): Promise<JsonApiResponse<CartegraphConfiguration>> {
    return firstValueFrom(
      this.http.put<JsonApiResponse<CartegraphConfiguration>>(
        `${environment.services.cartegraph}v1/config/settings/task-id-settings`,
        taskIdSettings
      ).pipe(
        catchError(HttpErrorHandler.handleError) // then handle the error
      ));
  }

  updateHourConfig(hourSettings: CartegraphHourSettings): Promise<JsonApiResponse<CartegraphConfiguration>> {
    return firstValueFrom(
      this.http.put<JsonApiResponse<CartegraphConfiguration>>(
        `${environment.services.cartegraph}v1/config/settings/hour-settings`,
        hourSettings
      ).pipe(
        catchError(HttpErrorHandler.handleError) // then handle the error
      ));
  }

  testConnection(baseUrl: string, username: string, password: string): Promise<JsonApiResponse<string>> {
    return firstValueFrom(this.http.patch<JsonApiResponse<string>>(`${environment.services.cartegraph}v1/config`,
      {
        baseUrl, username, password
      })
      .pipe(
        catchError(HttpErrorHandler.handleError) // then handle the error
      ));
  }

  fetchAllData(): Observable<JsonApiResponse<string>> {
    return this.http.post<JsonApiResponse<string>>(
      `${environment.services.cartegraph}v1/fetch`,
      {})
      .pipe(
        catchError((err) => {
          if (err?.status === 504) {
            // hide the 504 gateway timeout error
            return EMPTY;
          } else {
            // then handle the error
            return HttpErrorHandler.handleError(err);
          }
        })
      );
  }

  fetchIsRunning(): Observable<JsonApiResponse<boolean>> {
    return this.http.post<JsonApiResponse<boolean>>(
      `${environment.services.cartegraph}v1/fetch/status`,
      {})
      .pipe(
        catchError(HttpErrorHandler.handleError) // then handle the error
      );
  }

  getDatasetStatus(): Promise<JsonApiResponse<boolean>> {
    return firstValueFrom(this.http.get<JsonApiResponse<boolean>>(`${environment.services.cartegraph}v1/fetch/data-status`)
      .pipe(
        catchError((err) => {
          // console.log('err obj', err);
          // console.log('err includes?', err?.error?.error.includes('No data fetched yet'));
          if (err?.status === 400 && err?.error?.error.includes('No data fetched yet')) {
            return EMPTY;
          } else {
            // then handle the error
            return HttpErrorHandler.handleError(err);
          }
        })
      ));
  }

  importAllData(fetchAllData: boolean = false): Promise<JsonApiResponse<string>> {
    return firstValueFrom(this.http.post<JsonApiResponse<string>>(
      `${environment.services.cartegraph}v1/import`,
      {},
      {
        params: {
          'fetch-from-server': fetchAllData
        }
      })
      .pipe(
        catchError(HttpErrorHandler.handleError) // then handle the error
      ));
  }

  importDrivers(): Promise<JsonApiResponse<{ key: string, list: ImportResultRow[] }>> {
    return firstValueFrom(this.http.post<JsonApiResponse<{ key: string, list: ImportResultRow[] }>>(
      `${environment.services.cartegraph}v1/import/drivers`,
      {})
      .pipe(
        catchError(HttpErrorHandler.handleError) // then handle the error
      ));
  }

  importVehicles(): Promise<JsonApiResponse<{ key: string, list: ImportResultRow[] }>> {
    return firstValueFrom(this.http.post<JsonApiResponse<{ key: string, list: ImportResultRow[] }>>(
      `${environment.services.cartegraph}v1/import/vehicles`,
      {})
      .pipe(
        catchError(HttpErrorHandler.handleError) // then handle the error
      ));
  }

  // tslint:disable-next-line:no-shadowed-variable
  fetchLabor(filter: string): Promise<JsonApiResponse<LaborClass[]>> {
    return firstValueFrom(this.http.post<JsonApiResponse<LaborClass[]>>(
      `${environment.services.cartegraph}v1/labor`,
      {filter})
      .pipe(
        catchError(HttpErrorHandler.handleError) // then handle the error
      ));
  }

  // tslint:disable-next-line:no-shadowed-variable
  fetchEquipment(filter: string): Promise<JsonApiResponse<EquipmentClass[]>> {
    return firstValueFrom(this.http.post<JsonApiResponse<EquipmentClass[]>>(
      `${environment.services.cartegraph}v1/equipment`,
      {filter})
      .pipe(
        catchError(HttpErrorHandler.handleError) // then handle the error
      ));
  }

  // tslint:disable-next-line:no-shadowed-variable
  fetchBaseObject(cgClassName: string, filter: string): Promise<JsonApiResponse<BaseObjectClass[]>> {
    return firstValueFrom(this.http.post<JsonApiResponse<BaseObjectClass[]>>(
      `${environment.services.cartegraph}v1/baseObject`,
      {
        cgClassName,
        filter
      })
      .pipe(
        catchError(HttpErrorHandler.handleError) // then handle the error
      ));
  }

  getDatasetList(listRequest: CgDatasetListRequest[]): Promise<JsonApiResponse<CgDatasetList[]>> {
    return firstValueFrom(this.http.post<JsonApiResponse<CgDatasetList[]>>(
      `${environment.services.cartegraph}v1/dataset/value-list`,
      listRequest)
      .pipe(
        catchError(HttpErrorHandler.handleError) // then handle the error
      ));
  }

  getDatasetClassFields(listRequest: CgDatasetClassFieldRequest[]): Promise<JsonApiResponse<CgDatasetClassField[]>> {
    return firstValueFrom(this.http.post<JsonApiResponse<CgDatasetClassField[]>>(
      `${environment.services.cartegraph}v1/dataset/class-fields`,
      listRequest)
      .pipe(
        catchError(HttpErrorHandler.handleError) // then handle the error
      ));
  }

  getTaskList(pageIndex: number, size: number, statuses: string, searchText?: string, sort?: string) {
    let params = {
      size,
      page: pageIndex,
      status: statuses
    };

    if (!!sort) {
      params = Object.assign(params, {sort});
    }

    if (!!searchText) {
      params = Object.assign(params, {search: searchText});
    }

    return firstValueFrom(this.http.get<JsonApiResponse<Page<CartegraphSyncTask>>>(
      `${environment.services.cartegraph}v1/tasks`, {params})
      .pipe(
        catchError(HttpErrorHandler.handleError) // then handle the error
      ));
  }

  updateSyncTask(syncTaskId: number, action: string, body: any = null) {
    // content type is required if body is null
    return this.http.put<JsonApiResponse<string>>(
      `${environment.services.cartegraph}v1/tasks/${syncTaskId}`,
      body,
      {
        params: {action},
        headers: {
          'Content-Type': 'application/json'
        }
      }
    )
      .pipe(
        catchError(HttpErrorHandler.handleError) // then handle the error
      );
  }

  getImportTaskList(pageIndex: number, size: number, statuses: string[], sort?: string) {
    let params = {
      size,
      page: pageIndex,
      status: statuses.join(','),
    };

    if (sort) {
      params = Object.assign(params, {sort});
    }

    return this.http.get<JsonApiResponse<Page<CgImportTask>>>(
      `${environment.services.cartegraph}v1/import`,
      {
        params
      }
    )
      .pipe(
        catchError(HttpErrorHandler.handleError) // then handle the error
      );
  }

  getActivityList(includeSystem: boolean = false): Promise<JsonApiResponse<CgDatasetEntry[]>> {
    return firstValueFrom(this.http.get<JsonApiResponse<CgDatasetEntry[]>>(
      `${environment.services.cartegraph}v1/dataset/activity`, {params: {includeSystem}})
      .pipe(
        catchError(HttpErrorHandler.handleError) // then handle the error
      ));
  }

  getMaterialList(): Promise<JsonApiResponse<Material[]>> {
    return firstValueFrom(this.http.get<JsonApiResponse<Material[]>>(
      `${environment.services.cartegraph}v1/material/list`, {params: {deleted: false}})
      .pipe(
        catchError(HttpErrorHandler.handleError) // then handle the error
      ));
  }

  getMaterialLocationList(): Promise<JsonApiResponse<MaterialLocation[]>> {
    return firstValueFrom(this.http.get<JsonApiResponse<MaterialLocation[]>>(
      `${environment.services.cartegraph}v1/material-location`)
      .pipe(
        catchError(HttpErrorHandler.handleError) // then handle the error
      ));
  }

  getAttachmentList(): Promise<JsonApiResponse<VehicleEquipment[]>> {
    return firstValueFrom(this.http.get<JsonApiResponse<VehicleEquipment[]>>(
      `${environment.services.cartegraph}v1/vehicle-equipment`)
      .pipe(
        catchError(HttpErrorHandler.handleError) // then handle the error
      ));
  }

}
